/*
 * <global-model-selector>
 * Front-end only model picker for the header/footer "SELECT MODEL" drawer.
 * No network: it uses static Liquid-rendered metaobject data.
 */
const PMS_STORAGE_KEY = 'seacase_selected_phone_model';
const PMS_LEGACY_STORAGE_KEY = 'seacase_selected_model';
const PMS_GLOBAL_LABEL_SELECTOR = '[data-seacase-phone-model-label]';
const PMS_FALLBACK_LABEL = 'SELECT MODLE';
const PMS_CHANGE_EVENT = 'seacase:phone-model-change';
const PMS_LEGACY_CHANGE_EVENT = 'seacase:model-changed';

function pmsIsThemeEditorPreview() {
  return Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
}

function pmsNormalizeModel(model) {
  if (!model || typeof model !== 'object') return null;

  const handle = String(model.handle || '').trim();
  const name = String(model.name || '').trim();

  if (!handle || !name) return null;

  return { handle, name };
}

function pmsParseModel(raw) {
  if (!raw) return null;

  try {
    return pmsNormalizeModel(JSON.parse(raw));
  } catch (e) {
    return null;
  }
}

function pmsReadLocalSelection() {
  let raw = null;

  try {
    raw = localStorage.getItem(PMS_STORAGE_KEY);
  } catch (e) {
    return null;
  }

  const model = pmsParseModel(raw);

  if (!model && raw) {
    try {
      localStorage.removeItem(PMS_STORAGE_KEY);
    } catch (e) {
      /* localStorage unavailable - ignore */
    }
  }

  return model;
}

function pmsWriteLocalSelection(model) {
  const normalized = pmsNormalizeModel(model);
  if (!normalized) return null;

  try {
    localStorage.setItem(PMS_STORAGE_KEY, JSON.stringify(normalized));
  } catch (e) {
    /* localStorage unavailable - keep the page usable */
  }

  return normalized;
}

function pmsClearLocalSelection() {
  try {
    localStorage.removeItem(PMS_STORAGE_KEY);
  } catch (e) {
    /* localStorage unavailable - ignore */
  }
}

function pmsReadLegacySelection() {
  try {
    return pmsParseModel(sessionStorage.getItem(PMS_LEGACY_STORAGE_KEY));
  } catch (e) {
    return null;
  }
}

function pmsWriteLegacySelection(model) {
  const normalized = pmsNormalizeModel(model);
  if (!normalized) return null;

  try {
    sessionStorage.setItem(PMS_LEGACY_STORAGE_KEY, JSON.stringify(normalized));
  } catch (e) {
    /* sessionStorage unavailable - ignore */
  }

  return normalized;
}

function pmsWhenReady(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

if (!customElements.get('global-model-selector')) {
  customElements.define(
    'global-model-selector',
    class GlobalModelSelector extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.js-model-selector-search');
        this.groups = Array.from(this.querySelectorAll('.js-model-selector-group'));

        if (this.groups.length === 0) return;

        this.items = Array.from(this.querySelectorAll('.js-model-selector-item'));

        // Cache the searchable text for each group/item once.
        this.entries = this.groups.map((group) => {
          const label = group.querySelector('.js-model-selector-label');
          const items = Array.from(group.querySelectorAll('.js-model-selector-item'));
          return {
            group,
            brand: (label ? label.textContent : '').trim().toLowerCase(),
            items: items.map((item) => ({
              item,
              text: (item.dataset.item || item.textContent || '').trim().toLowerCase(),
            })),
          };
        });

        if (this.input) {
          this.handleInput = this.handleInput.bind(this);
          this.input.addEventListener('input', this.handleInput);
        }

        this.handleItemClick = this.handleItemClick.bind(this);
        this.addEventListener('click', this.handleItemClick);

        this.isProductSelector = this.hasAttribute('data-auto-select-default');
        this.onPhoneModelChanged = this.onPhoneModelChanged.bind(this);
        this.onLegacyModelChanged = this.onLegacyModelChanged.bind(this);
        this.onStorageChanged = this.onStorageChanged.bind(this);

        if (this.isProductSelector) {
          document.addEventListener(PMS_LEGACY_CHANGE_EVENT, this.onLegacyModelChanged);
        } else {
          window.addEventListener(PMS_CHANGE_EVENT, this.onPhoneModelChanged);
          window.addEventListener('storage', this.onStorageChanged);
        }

        pmsWhenReady(() => {
          if (this.isProductSelector) {
            this.autoSelectDefault();
          } else {
            this.initializeGlobalSelection();
          }
        });
      }

      disconnectedCallback() {
        this.input?.removeEventListener('input', this.handleInput);
        this.removeEventListener('click', this.handleItemClick);
        window.removeEventListener(PMS_CHANGE_EVENT, this.onPhoneModelChanged);
        window.removeEventListener('storage', this.onStorageChanged);
        document.removeEventListener(PMS_LEGACY_CHANGE_EVENT, this.onLegacyModelChanged);
      }

      handleInput() {
        const keyword = this.input.value.trim().toLowerCase();

        this.entries.forEach(({ group, brand, items }) => {
          const brandMatched = keyword !== '' && brand.includes(keyword);
          let hasVisibleItem = false;

          items.forEach(({ item, text }) => {
            const matched = keyword === '' || brandMatched || text.includes(keyword);
            item.hidden = !matched;
            if (matched) hasVisibleItem = true;
          });

          // Hide the whole brand group only when searching and nothing matched.
          group.hidden = keyword !== '' && !hasVisibleItem;
        });
      }

      handleItemClick(event) {
        const item = event.target.closest('.js-model-selector-item');
        if (!item || !this.contains(item)) return;

        const name = (item.dataset.item || item.textContent || '').trim();
        const handle = item.dataset.modelHandle || '';

        if (this.isProductSelector) {
          this.selectLegacyModel({ handle, name });
        } else {
          this.selectGlobalModel({ handle, name });
        }

        // Let <a> items navigate normally; for <button> items just close the drawer.
        if (item.tagName !== 'A') {
          this.closest('dialog-component')?.closeDialog?.();
        }
      }

      getDefaultModel() {
        return pmsParseModel(this.dataset.defaultPhoneModel || '');
      }

      getAvailableModel(model) {
        const normalized = pmsNormalizeModel(model);
        if (!normalized || !this.items || this.items.length === 0) return null;

        const item = this.items.find((candidate) => (candidate.dataset.modelHandle || '') === normalized.handle);
        if (!item) return null;

        return {
          handle: normalized.handle,
          name: (item.dataset.item || item.textContent || normalized.name).trim(),
        };
      }

      selectGlobalModel(model) {
        const selected = this.getAvailableModel(model);
        if (!selected) return;

        pmsWriteLocalSelection(selected);
        pmsWriteLegacySelection(selected);
        this.applyGlobalSelection(selected);
        this.broadcastGlobalSelection(selected, 'user');
      }

      selectLegacyModel(model) {
        const selected = this.getAvailableModel(model);
        if (!selected) return;

        pmsWriteLocalSelection(selected);
        pmsWriteLegacySelection(selected);
        this.applyProductSelection(selected);
        this.broadcastGlobalSelection(selected, 'user');
      }

      broadcastGlobalSelection(model, source = 'user') {
        const selected = this.getAvailableModel(model);
        if (!selected) return;
        if (pmsIsThemeEditorPreview()) return;
        const detail = { ...selected, source };
        window.dispatchEvent(new CustomEvent(PMS_CHANGE_EVENT, { detail }));
        document.dispatchEvent(new CustomEvent(PMS_LEGACY_CHANGE_EVENT, { detail }));
      }

      onPhoneModelChanged(event) {
        const selected = this.getAvailableModel(event.detail);
        if (!selected) {
          pmsClearLocalSelection();
          this.initializeGlobalSelection();
          return;
        }

        this.applyGlobalSelection(selected);
      }

      onLegacyModelChanged(event) {
        const source = event.detail && event.detail.source;
        if (
          this.hasNativeVariantDeepLink() &&
          (source === 'initialization' || source === 'native-variant-sync')
        ) {
          return;
        }

        const selected = this.getAvailableModel(event.detail);
        if (!selected) return;

        if (this.isProductSelector) {
          this.applyProductSelection(selected);
        } else {
          this.applyGlobalSelection(selected);
        }
      }

      onStorageChanged(event) {
        if (event.key !== PMS_STORAGE_KEY) return;

        const selected = this.getAvailableModel(pmsParseModel(event.newValue));
        if (selected) {
          window.dispatchEvent(
            new CustomEvent(PMS_CHANGE_EVENT, { detail: { ...selected, source: 'storage' } })
          );
        } else {
          this.initializeGlobalSelection();
        }
      }

      markSelected(handle) {
        if (!this.items) return;
        this.items.forEach((item) => {
          const matched = handle && (item.dataset.modelHandle || '') === handle;
          item.classList.toggle('is-selected', matched);
        });
      }

      updateGlobalLabels(name) {
        const labelText = String(name || PMS_FALLBACK_LABEL).trim() || PMS_FALLBACK_LABEL;
        document.querySelectorAll(PMS_GLOBAL_LABEL_SELECTOR).forEach((el) => {
          el.textContent = labelText;
        });
      }

      updateProductLabel(name) {
        if (!name) return;

        const scope = this.closest('[data-pi-model]') || this;
        scope.querySelectorAll('.footer-rm__select-label').forEach((el) => {
          el.textContent = name;
        });
      }

      applyGlobalSelection(model) {
        const selected = pmsNormalizeModel(model);
        if (!selected) return;

        this.markSelected(selected.handle);
        this.updateGlobalLabels(selected.name);
      }

      applyProductSelection(model) {
        const selected = pmsNormalizeModel(model);
        if (!selected) return;

        this.markSelected(selected.handle);
        this.updateProductLabel(selected.name);
      }

      initializeGlobalSelection() {
        if (!this.items || this.items.length === 0) return;

        const stored = pmsReadLocalSelection();
        if (stored) {
          const selected = this.getAvailableModel(stored);
          if (selected) {
            pmsWriteLocalSelection(selected);
            pmsWriteLegacySelection(selected);
            this.applyGlobalSelection(selected);
            this.broadcastGlobalSelection(selected, 'initialization');
            return;
          }

          pmsClearLocalSelection();
        }

        const defaultModel = this.getAvailableModel(this.getDefaultModel());
        if (!defaultModel) {
          this.markSelected('');
          this.updateGlobalLabels(PMS_FALLBACK_LABEL);
          return;
        }

        pmsWriteLocalSelection(defaultModel);
        pmsWriteLegacySelection(defaultModel);
        this.applyGlobalSelection(defaultModel);
        this.broadcastGlobalSelection(defaultModel, 'initialization');
      }

      hasNativeVariantDeepLink() {
        return Boolean(
          this.isProductSelector &&
            this.closest('pi-variant')?.hasAttribute('data-pi-native-variant-deep-link')
        );
      }

      syncProductSelectionFromVariant() {
        const scope = this.closest('[data-pi-model]');
        const currentName = String((scope && scope.dataset.piCurrent) || '').trim().toLowerCase();
        const item =
          this.items.find((candidate) => candidate.classList.contains('is-selected')) ||
          this.items.find(
            (candidate) =>
              String(candidate.dataset.item || candidate.textContent || '').trim().toLowerCase() ===
              currentName
          );
        if (!item) return false;

        const selected = this.getAvailableModel({
          handle: item.dataset.modelHandle || '',
          name: (item.dataset.item || item.textContent || '').trim(),
        });
        if (!selected) return false;

        pmsWriteLocalSelection(selected);
        pmsWriteLegacySelection(selected);
        this.applyProductSelection(selected);
        this.broadcastGlobalSelection(selected, 'native-variant-sync');
        return true;
      }

      // Product drawer: prefer the stored model if it's available here, else fall back to the first.
      autoSelectDefault() {
        if (!this.items || this.items.length === 0) return;

        if (this.hasNativeVariantDeepLink() && this.syncProductSelectionFromVariant()) {
          return;
        }

        const stored = pmsReadLocalSelection() || pmsReadLegacySelection();
        let chosen = null;

        if (stored) {
          chosen = this.getAvailableModel(stored);
        }

        if (!chosen) {
          const first = this.items[0];
          chosen = {
            handle: first.dataset.modelHandle || '',
            name: (first.dataset.item || first.textContent || '').trim(),
          };
        }

        pmsWriteLocalSelection(chosen);
        pmsWriteLegacySelection(chosen);
        this.applyProductSelection(chosen);
        this.broadcastGlobalSelection(chosen, 'initialization');
      }
    }
  );
}
